<script setup>
import { computed } from 'vue'
import ReportPage from '../components/ReportPage.vue'
import StatusTag from '../components/StatusTag.vue'
import BarChart from '../components/BarChart.vue'
import CoverageProgress from '../components/CoverageProgress.vue'
import AnatomyFigure from '../components/AnatomyFigure.vue'
import { formatWon } from '../formatters.js'

const props = defineProps({
  module: { type: Object, required: true },
  report: { type: Object, required: true },
  index: { type: Number, required: true },
  total: { type: Number, required: true }
})

const history = computed(() => props.module.statistics?.history || null)
const benchmark = computed(() => props.module.statistics?.benchmark || null)
const benchmarkPoints = computed(() => (benchmark.value?.values || []).map(item => ({ label: item.label, year: item.year, value: item.valueWon })))
const benefit = computed(() => props.module.demoCoverageCode ? props.report.insurance.coverages.find(item => item.code === props.module.demoCoverageCode) : null)
const moduleState = computed(() => {
  const states = props.module.healthAreas.map(area => area.state)
  if (states.includes('action')) return 'action'
  if (states.includes('watch')) return 'watch'
  if (states.includes('good')) return 'good'
  return 'unknown'
})
</script>

<template>
  <ReportPage
    :section="`disease-${module.id}`"
    eyebrow="05 · 질환별 건강과 보장"
    :title="`${module.title} — 건강과 보장`"
    :customer-name="report.customer.name"
  >
    <div class="dz">
      <div class="dz-h">
        <span class="dz-age">{{ report.customer.age }}세</span><span class="dz-n">{{ index + 1 }} / {{ total }}</span>
        <h2>{{ module.title }}</h2>
        <div class="dz-dz">비교 질환 <b>{{ module.diseaseLabel }}</b> <small>{{ module.diseaseIcd }}</small></div>
        <div class="dz-body"><AnatomyFigure :areas="module.healthAreas" :highlight-position="module.position" /></div>
      </div>

      <div class="dz-grid">
        <div class="dz-col">
          <h3>나의 건강 현황</h3>
          <ul v-if="module.healthAreas.length" class="hl">
            <li v-for="area in module.healthAreas" :key="area.id"><span>{{ area.label }}</span><StatusTag :state="area.state"  /><b :class="`st-${area.state}-t`">{{ area.result }}</b></li>
          </ul>
          <div v-else class="nocov">이 질환을 직접 평가한 검사 항목이 없습니다.</div>
          <p class="cc">{{ module.healthHint }}</p>
        </div>

        <div class="dz-col">
          <h3>통계로 보는 발생 현황 <small>· {{ history?.scope || '' }}</small></h3>
          <template v-if="history?.points?.length">
            <div class="chart-title">{{ history.title }}</div>
            <BarChart :points="history.points" :unit="history.unit" />
            <p v-if="history.note" class="cnote">{{ history.note }}</p>
          </template>
          <div v-else class="nocov">연결된 공개 통계가 없습니다.</div>
        </div>

        <div class="dz-col">
          <h3>나의 관련 보장 · {{ module.benefitLabel }}</h3>
          <div v-if="module.coreCoverages.length" class="covs">
            <CoverageProgress v-for="item in module.coreCoverages" :key="item.code" :item="item" />
          </div>
          <div v-else class="nocov">권장 비교항목에 연결된 보장이 없습니다.<br>임의로 권장 금액을 만들지 않습니다.</div>
          <p class="cc">{{ module.insuranceNote }}</p>
        </div>
      </div>

      <div class="dz-bottom">
        <div class="dz-col">
          <h3>공개 의료비 통계 <small>· {{ benchmark?.scope || '' }}</small></h3>
          <template v-if="benchmarkPoints.length">
            <div class="chart-title">{{ benchmark.label }}</div>
            <BarChart :points="benchmarkPoints" :unit="'만원 / 1인'" :value-scale="10000" /><p class="cnote">{{ benchmark.note }}</p>
            <div v-if="benefit" class="benefit-reference"><span>참고 · 보유 {{ module.benefitLabel }}</span><b>{{ formatWon(benefit.currentWon) }}</b></div>
          </template>
          <div v-else class="nocov">연결된 의료비 통계가 없습니다.</div>
        </div>
        <div class="dz-col">
          <h3>관련 보장항목 더 보기</h3>
          <table v-if="module.coverages.length" class="mini">
            <thead><tr><th>보장항목</th><th class="r">보유</th><th class="r">권장</th></tr></thead>
            <tbody><tr v-for="item in module.coverages" :key="item.code"><td>{{ item.name }}</td><td class="r"><b :class="item.status === 'ENOUGH' ? 'ok' : 'lack'">{{ formatWon(item.currentWon) }}</b></td><td class="r">{{ formatWon(item.targetWon) }}</td></tr></tbody>
          </table>
          <div v-else class="nocov">위 보장 외 추가 비교항목이 없습니다.</div>
        </div>
      </div>
    </div>
    <p class="page-note"><b>대표 질환 {{ module.diseaseLabel }}</b> — 검진 소견이 이 질환의 진단을 뜻하지 않습니다. 공개 통계는 개인의 발병 확률이나 보험금 지급 확약이 아니며, 같은 보장이 여러 질환 페이지에 반복될 수 있으므로 금액을 더하지 마세요.</p>
  </ReportPage>
</template>
